import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { fmtMoney, todayISO } from '../lib/format';
import { Button, Callout, Dialog, Field, Input, Select, useToast } from './ui';

export const PAYMENT_METHODS = [
  ['bank_transfer', 'העברה בנקאית'],
  ['cash', 'מזומן'],
  ['cheque', 'צ׳ק'],
  ['credit_card', 'כרטיס אשראי'],
  ['bit', 'ביט / אפליקציה'],
  ['other', 'אחר'],
] as const;

/** Every query whose numbers change when an invoice becomes paid. */
export function invalidatePayments(qc: ReturnType<typeof useQueryClient>) {
  for (const k of ['documents', 'document', 'customer', 'customers', 'dashboard', 'cashflow', 'sales']) qc.invalidateQueries({ queryKey: [k] });
}

/**
 * Marks one or more invoices as paid when no receipt was issued in Morning.
 * `invoices` carry the open amount so the user sees exactly what is being closed.
 */
export function MarkPaidDialog({
  invoices,
  onClose,
  onDone,
}: {
  invoices: Array<{ id: number; number: string | null; open: number | null; customerName?: string | null }>;
  onClose: () => void;
  onDone?: () => void;
}) {
  const [paidDate, setPaidDate] = useState(todayISO());
  const [method, setMethod] = useState<string>('bank_transfer');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();
  const sum = invoices.reduce((s, i) => s + (i.open ?? 0), 0);
  const single = invoices.length === 1 ? invoices[0] : null;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api<{ marked: number; skipped: number }>('/api/invoices/mark-paid', {
        body: { ids: invoices.map((i) => i.id), paidDate, paymentMethod: method, note: note.trim() || null },
      });
      invalidatePayments(qc);
      toast.success(r.marked === 1 ? 'החשבונית סומנה כשולמה' : `${r.marked} חשבוניות סומנו כשולמו${r.skipped ? ` (${r.skipped} כבר היו משולמות)` : ''}`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={single ? `סימון חשבונית ${single.number ?? ''} כשולמה` : `סימון ${invoices.length} חשבוניות כשולמו`}
      footer={
        <>
          <Button variant="primary" onClick={submit} loading={busy} disabled={!paidDate}>
            סימון כשולם
          </Button>
          <Button onClick={onClose}>ביטול</Button>
        </>
      }
    >
      <div className="stack">
        <div className="row">
          <span className="muted">סכום שייסגר:</span>
          <strong className="tabular">{fmtMoney(sum)}</strong>
          {single?.customerName ? <span className="muted">· {single.customerName}</span> : null}
        </div>
        <div className="form-grid">
          <Field label="תאריך התשלום" required>
            <Input type="date" value={paidDate} max={todayISO()} onChange={(e) => setPaidDate(e.target.value)} />
          </Field>
          <Field label="אמצעי תשלום">
            <Select value={method} onChange={(e) => setMethod(e.target.value)}>
              {PAYMENT_METHODS.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </Select>
          </Field>
          <Field label="הערה" className="span-2">
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="למשל: אסמכתא 12345" />
          </Field>
        </div>
        <Callout tone="info">
          <span className="small">לתשלום שלא הופקה עבורו קבלה במורנינג. אם תתקבל בהמשך קבלה לאותה חשבונית — היא תחליף את הסימון אוטומטית.</span>
        </Callout>
      </div>
    </Dialog>
  );
}
